import { Link } from 'react-router';
import { useNow } from '@/shared/hooks/use-now';
import { useTranslation } from '@/shared/i18n/use-translation';
import { cn } from '@/shared/lib/cn';
import { formatAgo } from '@/shared/lib/dates/format-ago';
import { replica } from '@/shared/sync/replica';
import { usePendingCount } from '@/shared/sync/use-pending-count';
import { useSyncState } from '@/shared/sync/use-sync-state';
import { Tooltip } from '@/shared/ui/tooltip/tooltip';
import { TooltipContent } from '@/shared/ui/tooltip/tooltip-content';
import { TooltipTrigger } from '@/shared/ui/tooltip/tooltip-trigger';
import { describeSync } from '../helpers/describe-sync';
import { syncIcon } from '../helpers/sync-icon';

type SyncIndicatorProps = {
  readonly showLabel?: boolean;
  /** Add "· 5 min ago" after the label. */
  readonly showLastSync?: boolean;
  readonly className?: string;
};

/** The sync state; clicking syncs now (or goes to sign in when the session expired). */
export function SyncIndicator({ showLabel = true, showLastSync, className }: SyncIndicatorProps) {
  const { t } = useTranslation();
  const state = useSyncState();
  const now = useNow();
  const pending = usePendingCount() ?? 0;
  const { label, detail } = describeSync(state, pending);
  const { icon: Icon, tone } = syncIcon(state, pending);
  const ago = showLastSync && state.lastSyncAt ? formatAgo(state.lastSyncAt, now) : null;
  const classes = cn(
    'inline-flex min-h-10 min-w-10 items-center justify-center gap-2 rounded-lg px-2 py-1 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground md:min-h-0',
    className,
  );
  const trigger =
    state.status === 'unauthorized' ? (
      <Link to="/sign-in" className={classes} aria-label={`${label}. ${detail}`} />
    ) : (
      <button
        type="button"
        className={classes}
        onClick={() => void replica.engine.syncNow()}
        aria-label={`${label}. ${detail} ${t('Sync now')}`}
      />
    );
  return (
    <Tooltip>
      <TooltipTrigger render={trigger}>
        <Icon className={cn('size-4 shrink-0', tone)} aria-hidden />
        {showLabel && (
          <span className="truncate">
            {label}
            {ago && <span className="text-muted-foreground/80"> · {ago}</span>}
          </span>
        )}
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-72">
        {detail}
      </TooltipContent>
    </Tooltip>
  );
}
